import React, { useEffect, useMemo, useRef, useState } from "react";
import PayoutsAPI from "../../apis/PayoutsAPI.jsx";
import { portfolioAPI } from "../../apis/PortfolioAPI.jsx";

/* SendPayout — country/rail-agnostic payout UI.
   Loads the catalog from V2 first (GET /v2/payouts/routes) and falls
   back per-rail to V1 (GET /payments/payout/routes) for rails not yet
   migrated. Renders the form dynamically from each rail's field
   schema — adding a new country (Mexico/SPEI, Brazil/PIX) requires
   zero changes here, only a backend catalog entry.

   Per PAYOUTS_V2_FE_INTEGRATION.md §Migration the two ledgers are
   independent: a payout submitted via V2 uses /v2/payouts and ends up
   in the V2 ledger, V1 stays in /payments/history. Rails carry a
   _source flag ("v1" | "v2") so submit branches by origin. The user
   experience is unchanged. */

/* Merge V2 catalog (preferred) with V1 catalog (fallback). For any
   (country, rail_id) tuple, V2 wins if present — that's what enables
   per-rail migration. V1 fills in countries and rails V2 doesn't yet
   list. Preserves V1's display affordances (country_name, currencies,
   display_name) for rails that only exist on V1, while annotating V2
   rails with the same shape so downstream rendering is uniform. */
function mergeCatalogs(v2Routes, v1Routes) {
  const byCountry = new Map();

  // Seed with V1 first (lower priority).
  (v1Routes || []).forEach((c) => {
    const country = {
      country_code: c.country_code,
      country_name: c.country_name || c.country_code,
      currencies:   c.currencies || [],
      rails: (c.rails || []).map((r) => ({ ...r, _source: "v1" })),
    };
    byCountry.set(c.country_code, country);
  });

  // Overlay V2 (higher priority).
  (v2Routes || []).forEach((c) => {
    const existing = byCountry.get(c.country_code) || {
      country_code: c.country_code,
      country_name: c.country_code,    // V2 may omit country_name
      currencies:   [],
      rails: [],
    };
    const v2Rails = (c.rails || []).map((r) => ({
      ...r,
      _source: "v2",
      // For V2 rails the display name often equals the rail_id upper-
      // cased; fall back gracefully when the V2 catalog omits it.
      display_name: r.display_name || r.rail_id?.toUpperCase() || "—",
      // V2 rails carry method directly per spec example.
      method: r.method || "",
      provider: r.provider || r.provider_id || "",
      requires_2fa: !!r.requires_2fa,
      fields: r.fields || [],
    }));
    // V1 rails not present in V2 stay.
    const v2RailIds = new Set(v2Rails.map((r) => r.rail_id));
    const keptV1 = (existing.rails || []).filter((r) => !v2RailIds.has(r.rail_id));
    byCountry.set(c.country_code, {
      ...existing,
      country_name: existing.country_name || c.country_code,
      rails: [...v2Rails, ...keptV1],
    });
  });

  return Array.from(byCountry.values());
}

/* Extract the human-readable message from the V2 error envelope.
   V2 errors: { success:false, error:"CODE", message:"…" }. Older V1
   errors: { payload, reason, message }. This collapses both. */
function readError(err) {
  const body = err?.response?.data || err?.data || {};
  return body.message || body.payload || body.reason || body.error || err?.message || "Payout failed";
}

/* Two-column terminal layout — country/rail list (left) and the
   composer (center). The parent slot (PayScreen "global" mode)
   provides the right column with PayoutHistory. */

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize:9, letterSpacing:"0.18em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:6 }}>
      {children}
    </div>
  );
}

function FieldShell({ label, children, required }) {
  return (
    <div>
      <SectionLabel>{label}{required ? " ·" : ""}</SectionLabel>
      <div style={{ padding:"8px 10px", border:"1px solid var(--term-line)", background:"var(--term-bg-pane)" }}>
        {children}
      </div>
    </div>
  );
}

function RailField({ field, value, onChange, entities, loadingEntities }) {
  const label = field.label || field.name;
  const baseInput = {
    background:"transparent", border:"none", color:"var(--term-ink-1)",
    fontFamily:"inherit", fontSize:13, width:"100%", outline:"none",
  };

  if (field.type === "enum") {
    const opts = field.options_endpoint
      ? (entities || []).map((e) => ({ value: e.id || e.code, label: e.name || e.code }))
      : (field.options || []).map((o) => ({ value: o.value, label: o.label || o.value }));
    return (
      <FieldShell label={label} required={field.required}>
        <select
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...baseInput, appearance:"none", cursor:"pointer" }}
        >
          <option value="" style={{ background:"#0F1421", color:"var(--term-ink-3)" }}>
            {field.options_endpoint && loadingEntities ? "Loading…" : "— select —"}
          </option>
          {opts.map((o) => (
            <option key={o.value} value={o.value} style={{ background:"#0F1421", color:"var(--term-ink-1)" }}>
              {o.label}
            </option>
          ))}
        </select>
      </FieldShell>
    );
  }

  return (
    <FieldShell label={label} required={field.required}>
      <input
        type={field.type === "number" ? "number" : "text"}
        required={field.required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.validation || ""}
        style={baseInput}
      />
    </FieldShell>
  );
}

export default function SendPayout({ token }) {
  const [routes, setRoutes]               = useState([]);
  const [countryCode, setCountryCode]     = useState("");
  const [railId, setRailId]               = useState("");
  const [amount, setAmount]               = useState("");
  const [note, setNote]                   = useState("");
  const [code, setCode]                   = useState("");
  const [fieldValues, setFieldValues]     = useState({});
  const [entityCache, setEntityCache]     = useState({});
  const [entityLoading, setEntityLoading] = useState({});
  const [portfolioName, setPortfolioName] = useState("Main");
  const [portfolioId, setPortfolioId]     = useState(null);  // V2 submits use id, V1 uses name
  /* Idempotency key for the current submit attempt. Reused on
     transient-error retries (502/504 per spec); reset on a fresh
     Send click. Stored in a ref so it survives re-renders without
     forcing them. */
  const idemRef = useRef(null);
  const [loading, setLoading]             = useState(true);
  const [catalogError, setCatalogError]   = useState(null);
  const [submitting, setSubmitting]       = useState(false);
  const [result, setResult]               = useState(null);
  const [error, setError]                 = useState(null);

  /* Catalog (V2 + V1, merged) + portfolio in parallel on mount.
     Per spec §Migration the FE probes V2 first; whatever V2 returns
     replaces V1 per-rail. If V2 errors (legacy backend) we silently
     fall back to V1 alone — that's the rollout-safe path. If both
     error the user sees the V1 error message. */
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    let cancelled = false;
    Promise.all([
      PayoutsAPI.getRoutesV2(token).catch((e) => ({ _err: e })),
      PayoutsAPI.getRoutes(token).catch((e) => ({ _err: e })),
      portfolioAPI.getDefaultPortfolio(token).catch(() => null),
    ]).then(([v2, v1, def]) => {
      if (cancelled) return;
      const v2Routes = v2?._err ? [] : (v2?.data?.routes || []);
      const v1Routes = v1?._err ? [] : (v1?.data?.routes || []);
      const merged   = mergeCatalogs(v2Routes, v1Routes);

      if (merged.length === 0) {
        // Both surfaces empty / errored — surface the most useful
        // error message we have. Prefer V2 if both failed since V2
        // is the path forward.
        const err = (v2?._err && v1?._err) ? (v2._err || v1._err) : (v1?._err || v2?._err);
        setCatalogError(err ? readError(err) : "No payout routes enabled");
      } else {
        setRoutes(merged);
        setCountryCode(merged[0].country_code);
        if (merged[0].rails?.length > 0) setRailId(merged[0].rails[0].rail_id);
      }
      if (def?.data?.name) setPortfolioName(def.data.name);
      if (def?.data?.id || def?.data?.portfolio_id) setPortfolioId(def.data.id || def.data.portfolio_id);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [token]);

  const country = useMemo(() => routes.find((r) => r.country_code === countryCode) || null, [routes, countryCode]);
  const rail    = useMemo(() => country?.rails?.find((r) => r.rail_id === railId) || null, [country, railId]);

  /* When rail changes: clear field values + lazy-load any options_endpoint. */
  useEffect(() => {
    if (!rail) return;
    setFieldValues({});
    setResult(null);
    setError(null);
    setCode("");
    const needsEntities = rail.fields.filter((f) => f.options_endpoint);
    if (needsEntities.length === 0) return;
    if (entityCache[rail.rail_id]) return;       // session cache hit
    if (entityLoading[rail.rail_id]) return;     // already in flight
    setEntityLoading((m) => ({ ...m, [rail.rail_id]: true }));
    // Route the entities lookup to the same surface that owns the rail.
    const fetchEntities = rail._source === "v2"
      ? PayoutsAPI.getRailEntitiesV2(token, rail.rail_id)
      : PayoutsAPI.getRailEntities(token, rail.rail_id);
    fetchEntities
      .then((resp) => {
        setEntityCache((c) => ({ ...c, [rail.rail_id]: resp?.data?.entities || [] }));
      })
      .catch(() => {
        setEntityCache((c) => ({ ...c, [rail.rail_id]: [] }));
      })
      .finally(() => {
        setEntityLoading((m) => ({ ...m, [rail.rail_id]: false }));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railId]);

  /* After a V2 submit lands, poll GET /v2/payouts/{id} until terminal
     status per spec §Step 5. Exponential backoff capped at 30s.
     We don't block the UI on this — the success banner shows
     immediately on SUBMITTED, and the polled terminal status updates
     the banner copy when it arrives. Aborts when the form unmounts or
     the user starts another submit. */
  const pollV2Status = (payoutId, signal) => {
    let delay = 1000;
    const poll = () => {
      if (signal?.aborted) return;
      PayoutsAPI.getPayoutV2(token, payoutId)
        .then((r) => {
          const p = r?.data || {};
          if (["COMPLETED","FAILED","CANCELLED","EXPIRED"].includes(p.status)) {
            setResult((prev) => ({ ...(prev || {}), ...p }));
            return;
          }
          setResult((prev) => ({ ...(prev || {}), ...p }));
          delay = Math.min(delay * 1.5, 30_000);
          setTimeout(poll, delay);
        })
        .catch(() => {
          delay = Math.min(delay * 1.5, 30_000);
          setTimeout(poll, delay);
        });
    };
    setTimeout(poll, delay);
  };
  const pollAbortRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!rail || !country) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    // Abort any in-flight poll from a prior attempt.
    if (pollAbortRef.current) { try { pollAbortRef.current.abort(); } catch (_) {} }
    const abortCtl = (typeof AbortController !== "undefined") ? new AbortController() : { signal: { aborted: false }, abort() { this.signal.aborted = true; } };
    pollAbortRef.current = abortCtl;

    if (rail._source === "v2") {
      // V2 path: catalog-driven, idempotency-keyed, string amount.
      // Generate a fresh key per submit attempt; retries within an
      // attempt reuse it. On 422 IDEMPOTENCY_KEY_CONFLICT we generate
      // a new one and retry once.
      idemRef.current = PayoutsAPI.makeIdempotencyKey();
      const submitWith = (key) => PayoutsAPI.submitPayoutV2(
        token,
        {
          country:       country.country_code,
          currency:      country.currencies?.[0] || rail.currency || "",
          rail_id:       rail.rail_id,
          method:        rail.method || "",
          amount,
          portfolio_id:  portfolioId,
          recipient:     fieldValues,
          note,
          code:          rail.requires_2fa ? code : null,
        },
        key,
      );
      submitWith(idemRef.current)
        .then((resp) => {
          const data = resp?.data || {};
          if (data.success === false) {
            setError(readError({ response: { data } }));
          } else {
            setResult(data);
            if (data.payout_id) pollV2Status(data.payout_id, abortCtl.signal);
          }
          setSubmitting(false);
        })
        .catch((err) => {
          const status = err?.response?.status;
          const body   = err?.response?.data || {};
          // Same key, different body → backend cached a stale attempt.
          // Fresh key + one retry.
          if (status === 422 && body.error === "IDEMPOTENCY_KEY_CONFLICT") {
            idemRef.current = PayoutsAPI.makeIdempotencyKey();
            submitWith(idemRef.current)
              .then((resp) => {
                const d = resp?.data || {};
                if (d.success === false) setError(readError({ response: { data: d } }));
                else {
                  setResult(d);
                  if (d.payout_id) pollV2Status(d.payout_id, abortCtl.signal);
                }
                setSubmitting(false);
              })
              .catch((err2) => {
                setError(readError(err2));
                setSubmitting(false);
              });
            return;
          }
          setError(readError(err));
          setSubmitting(false);
        });
      return;
    }

    // V1 path — unchanged from prior behavior.
    PayoutsAPI.submitPayout(token, rail, {
      portfolio_name: portfolioName,
      currency:       country.currencies?.[0] || "",
      amount,
      note,
      code: rail.requires_2fa ? code : null,
      fields: fieldValues,
    })
      .then((resp) => {
        const data = resp?.data || {};
        if (data.success === false) {
          setError(data.payload || data.reason || data.error_code || "Payout failed");
        } else {
          setResult(data);
        }
        setSubmitting(false);
      })
      .catch((err) => {
        setError(readError(err));
        setSubmitting(false);
      });
  };

  /* States that don't render the form. */
  if (loading) {
    return (
      <div style={{ display:"grid", gridTemplateColumns:"360px 1fr", height:"100%" }}>
        <div className="term-pane"><div className="term-pane-head">Destinations</div><div className="mono" style={{ padding:"24px 14px", fontSize:11.5, color:"var(--term-ink-3)" }}>loading catalog…</div></div>
        <div className="term-pane"><div className="term-pane-head">Compose payout</div></div>
      </div>
    );
  }
  if (catalogError || routes.length === 0) {
    return (
      <div style={{ display:"grid", gridTemplateColumns:"360px 1fr", height:"100%" }}>
        <div className="term-pane"><div className="term-pane-head">Destinations</div></div>
        <div className="term-pane">
          <div className="term-pane-head">Compose payout</div>
          <div style={{ padding:"24px 18px", fontSize:13, color:"var(--term-ink-2)", lineHeight:1.6 }}>
            <div style={{ fontFamily:"Inter", fontSize:15, fontWeight:500, color:"var(--term-ink-1)", marginBottom:8 }}>
              {catalogError ? "Payout router unavailable" : "No payout routes enabled"}
            </div>
            <div style={{ fontSize:12, color:"var(--term-ink-3)" }}>
              {catalogError
                ? String(catalogError)
                : "Ask Bloxcross support to enable outbound payments for your account."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const submitCurrency = country?.currencies?.[0] || "";

  return (
    <form onSubmit={handleSubmit} style={{ display:"grid", gridTemplateColumns:"360px 1fr", height:"100%", minHeight:0 }}>
      {/* LEFT — country → rail picker */}
      <div className="term-pane">
        <div className="term-pane-head">
          Destinations
          <span className="meta"><b>{routes.length}</b> {routes.length === 1 ? "country" : "countries"}</span>
        </div>
        <div className="term-pane-body">
          {routes.map((c) => {
            const isActive = c.country_code === countryCode;
            return (
              <div key={c.country_code} style={{ borderBottom:"1px solid var(--term-line-dim)" }}>
                <div
                  onClick={() => {
                    setCountryCode(c.country_code);
                    if (c.rails?.length) setRailId(c.rails[0].rail_id);
                  }}
                  style={{
                    padding:"10px 14px", cursor:"pointer",
                    background: isActive ? "var(--term-hover-bg)" : "transparent",
                    position:"relative",
                  }}
                >
                  {isActive && <span style={{ position:"absolute", left:0, top:0, bottom:0, width:2, background:"var(--term-green)" }} />}
                  <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between" }}>
                    <span style={{ fontSize:13, fontWeight:500, letterSpacing:"-0.005em", color:"var(--term-ink-1)" }}>{c.country_name}</span>
                    <span className="mono" style={{ fontSize:10, color:"var(--term-ink-3)", letterSpacing:"0.06em" }}>{c.country_code} · {(c.currencies || []).join(", ")}</span>
                  </div>
                </div>
                {isActive && (c.rails || []).map((r) => {
                  const sel = r.rail_id === railId;
                  return (
                    <div
                      key={r.rail_id}
                      onClick={() => setRailId(r.rail_id)}
                      style={{
                        padding:"8px 14px 8px 28px",
                        cursor:"pointer",
                        background: sel ? "rgba(44,203,126,0.06)" : "transparent",
                        borderLeft: sel ? "2px solid var(--term-green)" : "2px solid transparent",
                      }}
                    >
                      <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8 }}>
                        <span style={{ fontSize:12, color: sel ? "var(--term-ink-1)" : "var(--term-ink-2)" }}>{r.display_name}</span>
                        <span className="mono" style={{ fontSize:9, color:"var(--term-ink-3)", letterSpacing:"0.08em", textTransform:"uppercase" }}>{r.method}</span>
                      </div>
                      <div className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", marginTop:2 }}>
                        {r.provider} {r.requires_2fa ? "· 2fa" : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* CENTER — composer */}
      <div className="term-pane" style={{ minHeight:0 }}>
        <div className="term-pane-head" style={{ height:"auto", alignItems:"flex-start", flexDirection:"column", gap:4, padding:"14px 18px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, width:"100%" }}>
            <span style={{ fontSize:16, fontWeight:500, letterSpacing:"-0.01em", color:"var(--term-ink-1)" }}>
              Compose payout
            </span>
            {rail && (
              <span className="mono" style={{ fontSize:10, color:"var(--term-ink-3)", marginLeft:"auto", letterSpacing:"0.06em" }}>
                {country?.country_code} · {rail.display_name} · {rail.provider}
              </span>
            )}
          </div>
          {rail && (
            <div style={{ fontSize:11.5, color:"var(--term-ink-2)", textTransform:"none", letterSpacing:"normal", fontWeight:400 }}>
              {rail.method}{rail.requires_2fa ? " · 2FA required" : ""}
            </div>
          )}
        </div>

        <div style={{ flex:1, overflow:"auto", padding:"14px 18px", display:"flex", flexDirection:"column", gap:12 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <FieldShell label={`Amount · ${submitCurrency}`} required>
              <input
                type="number"
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                style={{
                  background:"transparent", border:"none", color:"var(--term-ink-1)",
                  fontFamily:"JetBrains Mono", fontSize:18, fontWeight:500,
                  width:"100%", outline:"none",
                }}
              />
            </FieldShell>
            <FieldShell label="Note (optional)">
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Invoice ref / memo"
                style={{
                  background:"transparent", border:"none", color:"var(--term-ink-1)",
                  fontFamily:"inherit", fontSize:13, width:"100%", outline:"none",
                }}
              />
            </FieldShell>
          </div>

          <div style={{ borderTop:"1px solid var(--term-line-dim)", paddingTop:12, display:"flex", flexDirection:"column", gap:10 }}>
            <SectionLabel>Beneficiary details</SectionLabel>
            {rail?.fields?.map((f) => (
              <RailField
                key={f.name}
                field={f}
                value={fieldValues[f.name] ?? ""}
                onChange={(v) => setFieldValues((p) => ({ ...p, [f.name]: v }))}
                entities={entityCache[rail.rail_id]}
                loadingEntities={entityLoading[rail.rail_id]}
              />
            ))}
          </div>

          {rail?.requires_2fa && (
            <div style={{ borderTop:"1px solid var(--term-line-dim)", paddingTop:12 }}>
              <FieldShell label="2FA code" required>
                <input
                  type="text"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="6-digit code"
                  inputMode="numeric"
                  style={{
                    background:"transparent", border:"none", color:"var(--term-ink-1)",
                    fontFamily:"JetBrains Mono", fontSize:15, letterSpacing:"0.16em",
                    width:"100%", outline:"none",
                  }}
                />
              </FieldShell>
            </div>
          )}

          {result && (
            <div style={{ borderTop:"1px solid var(--term-line-dim)", paddingTop:12 }}>
              <div className="mono" style={{ fontSize:11, color:"var(--term-green)", padding:"10px 12px", border:"1px solid var(--term-green)", background:"rgba(44,203,126,0.06)" }}>
                {result.status ? `${result.status.toLowerCase()} · ` : "submitted · "}
                id {result.payout_id || result.id || "—"}
                {result.fee ? <> · fee {result.fee} {submitCurrency}</> : null}
                {(result.provider_id || result.provider) ? <> · provider {result.provider_id || result.provider}</> : null}
              </div>
            </div>
          )}
          {error && (
            <div style={{ borderTop:"1px solid var(--term-line-dim)", paddingTop:12 }}>
              <div className="mono" style={{ fontSize:11, color:"var(--term-red)", padding:"10px 12px", border:"1px solid var(--term-red)", background:"rgba(239,68,68,0.06)" }}>
                {String(error)}
              </div>
            </div>
          )}
        </div>

        <div style={{ borderTop:"1px solid var(--term-line)", padding:"12px 18px", display:"flex", justifyContent:"flex-end", gap:10, background:"var(--term-bg-pane)" }}>
          <span className="mono" style={{ fontSize:10, color:"var(--term-ink-3)", alignSelf:"center", letterSpacing:"0.04em" }}>
            from portfolio <b style={{ color:"var(--term-ink-1)" }}>{portfolioName}</b>
          </span>
          <button
            type="submit"
            disabled={submitting || !rail || !amount}
            style={{
              background: submitting || !amount ? "var(--term-line)" : "var(--term-green)",
              border: `1px solid ${submitting || !amount ? "var(--term-line)" : "var(--term-green)"}`,
              color: submitting || !amount ? "var(--term-ink-3)" : "#000",
              height:32, padding:"0 18px", fontWeight:600, letterSpacing:"0.04em", fontSize:12,
              cursor: submitting || !amount ? "not-allowed" : "pointer",
              fontFamily:"inherit",
            }}
          >
            {submitting ? "Sending…" : `Send ${amount ? Number(amount).toLocaleString("en-US") : "0"} ${submitCurrency}`}
          </button>
        </div>
      </div>
    </form>
  );
}
